import { Button, Select, Table, Spin } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import objectToQueryParam from "../utils/objectToQueryParam";
import { useQuery } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import CustomSelect from "./hoc/CustomSelect";
import { isArray } from "lodash";
import {
  applyDefaultSortToColumns,
  getControlledSort,
  hasPrimarySort,
  initialSortState,
} from "../utils/tableSortUtils";

const PageLimitSelect = ({ handleChange, value }) => (
  <CustomSelect
    value={value}
    onChange={handleChange}
    options={[
      {
        value: 10,
        label: "10 / Page",
      },
      {
        value: 20,
        label: "20 / Page",
      },
      {
        value: 50,
        label: "50 / Page",
      },
    ]}
  />
);

function CustomPaginatedTable({
  columns,
  query = "",
  queryKey,
  path,
  limit = 50,
  filterData = {},
  loadMoreButton,
  showLimitChanger = false,
  showIndexColumn = false,
  localFilterFunction,
  customDataMapper,
  getDataStats,
  tableProps = {},
  hidePagination = false,
  customRender,
  customWrapperClass,
  defaultSort,
  defaultSortToColumn,
}) {
  const [page, setPage] = useState(1);
  const [sortState, setSortState] = useState(() =>
    initialSortState(defaultSortToColumn),
  );

  const usesPrimarySort = hasPrimarySort(defaultSort);

  const mappedColumns = useMemo(
    () => columns.map((i) => ({ ...i, minWidth: 100 })),
    [columns],
  );

  // When a primary "first order" sort (e.g. is_paused) is provided we control the
  // sort ourselves so that grouping stays fixed and only the active column flips.
  // Otherwise fall back to antd's built-in local column sorting.
  const { tableColumns, sortData } = useMemo(() => {
    if (usesPrimarySort) {
      return getControlledSort({
        columns: mappedColumns,
        defaultSort,
        sortState,
      });
    }
    return {
      tableColumns: applyDefaultSortToColumns(mappedColumns, defaultSortToColumn),
      sortData: (data) => data,
    };
  }, [mappedColumns, defaultSort, defaultSortToColumn, sortState, usesPrimarySort]);

  const handleTableChange = (_pagination, _filters, sorter) => {
    const active = Array.isArray(sorter) ? sorter[0] : sorter;
    setSortState(
      active?.order
        ? { columnKey: active.columnKey ?? active.field, order: active.order }
        : null,
    );
  };

  const [pageLimit, setPageLimit] = useState(limit);
  const {
    data: dataSourceRaw,
    isLoading: loading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      ...queryKey,
      {
        params: objectToQueryParam({
          page: page - 1,
          limit: pageLimit,
          query,
          ...filterData,
        }),
      },
    ],
    // queryFn: ({ signal, queryKey }) => fetchEvents({ signal, ...queryKey[1] }),
    queryFn: () =>
      queryApi(
        `${path}${objectToQueryParam({
          page: page - 1,
          limit: pageLimit,
          query,
          ...filterData,
        })}`,
        "GET",
      ),
    staleTime: Infinity,
    gcTime: 0,
  });

  const dataSource = !!customDataMapper
    ? customDataMapper(dataSourceRaw ?? [])
    : dataSourceRaw;

  useEffect(() => {
    if (getDataStats && isArray(dataSource)) {
      getDataStats(dataSource);
    }
  }, [dataSourceRaw]);

  const filteredData = !!localFilterFunction
    ? localFilterFunction(dataSource)
    : dataSource;

  const sortedData = useMemo(
    () => sortData(filteredData),
    [sortData, filteredData],
  );

  // If customRender is provided, render custom list view
  if (customRender) {
    return (
      <>
        {loading ? (
          <div
            style={{
              padding: 20,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              flex: 1,
            }}
          >
            <Spin />
          </div>
        ) : (
          <div
            className={customWrapperClass}
            style={{
              flex: 1,
              height: "100%",
              overflow: "auto",
            }}
          >
            <>
              {isArray(sortedData) && sortedData.length > 0 ? (
                sortedData.map((item, index) =>
                  customRender(item, index, (page - 1) * pageLimit + index),
                )
              ) : (
                <div
                  className="app-muted"
                  style={{ padding: 20, textAlign: "center" }}
                >
                  No data available
                </div>
              )}
            </>
          </div>
        )}
        {!hidePagination && (
          <div
            className="p-2"
            style={{
              textAlign: loadMoreButton ? "center" : "right",
              flex: "0 0 auto",
            }}
          >
            <>
              <Button
                icon={<LeftOutlined />}
                type="text"
                size="small"
                style={{ marginRight: 12 }}
                onClick={() => setPage((prev) => prev - 1)}
                disabled={page === 1}
              />
              <Button
                icon={<RightOutlined />}
                type="text"
                size="small"
                onClick={() => {
                  setPage((prev) => prev + 1);
                }}
                disabled={sortedData?.length < pageLimit}
              />
              {showLimitChanger && (
                <PageLimitSelect
                  value={pageLimit}
                  handleChange={(e) => {
                    setPage(1);
                    setPageLimit(e);
                  }}
                />
              )}
            </>
          </div>
        )}
      </>
    );
  }

  // Default table view
  return (
    <>
      <div
        style={{
          flex: 1 /* Make the table container take up remaining space */,
          overflow: "auto",
        }}
        className="bg-transparent"
      >
        <Table
          {...tableProps}
          columns={
            showIndexColumn
              ? [
                  {
                    title: "#",
                    key: "#",
                    render: (item, record, index) =>
                      (page - 1) * pageLimit + index + 1,
                  },
                  ...tableColumns,
                ]
              : tableColumns
          }
          dataSource={sortedData}
          size="small"
          loading={loading}
          pagination={false}
          onChange={handleTableChange}
        />
      </div>
      {!hidePagination && (
        <div
          className="p-2"
          style={{
            textAlign: loadMoreButton ? "center" : "right",
            flex: "0 0 auto",
          }}
        >
          <>
            <Button
              icon={<LeftOutlined />}
              type="text"
              size="small"
              style={{ marginRight: 12 }}
              onClick={() => setPage((prev) => prev - 1)}
              disabled={page === 1}
            />
            <Button
              icon={<RightOutlined />}
              type="text"
              size="small"
              onClick={() => {
                setPage((prev) => prev + 1);
              }}
              disabled={sortedData?.length < pageLimit}
            />
            {showLimitChanger && (
              <PageLimitSelect
                value={pageLimit}
                handleChange={(e) => {
                  setPage(1);
                  setPageLimit(e);
                }}
              />
            )}
          </>
        </div>
      )}
    </>
  );
}

export default CustomPaginatedTable;
